import * as XLSX from "xlsx";
import { storeGroupingtypes, storeMastShortNameKey, storeMastStoreCodeKey } from "../../data";

const GROUP_KEYS = {
	to_allocate: storeGroupingtypes.to_allocate.key,
	to_exclude: storeGroupingtypes.to_exclude.key,
	foc_stores: storeGroupingtypes.foc_stores.key,
};

const HEADER_ALIASES = {
	[GROUP_KEYS.to_allocate]: ["to allocate", "to_allocate", "allocate", "stores to allocate"],
	[GROUP_KEYS.to_exclude]: ["to exclude", "to_exclude", "exclude", "stores to exclude"],
	[GROUP_KEYS.foc_stores]: ["foc stores", "foc_stores", "foc", "foc store"],
};

const SAMPLE_HEADERS = [
	storeGroupingtypes.to_allocate.name,
	storeGroupingtypes.to_exclude.name,
	storeGroupingtypes.foc_stores.name,
];

export const normalizeHeader = (value) =>
	String(value ?? "")
		.toLowerCase()
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();

export const normalizeStoreCode = (value) => {
	if (value === null || value === undefined) return "";
	let text = String(value).trim();
	if (!text || ["nan", "null", "undefined"].includes(text.toLowerCase())) return "";
	if (/^\d+\.0$/.test(text)) text = text.slice(0, -2);
	return text;
};

const matchHeaderToKey = (header) => {
	const normalized = normalizeHeader(header);
	if (!normalized) return null;
	return (
		Object.keys(HEADER_ALIASES).find(
			(key) => HEADER_ALIASES[key].includes(normalized) || normalized === normalizeHeader(storeGroupingtypes[key]?.name)
		) || null
	);
};

const uniqueCodes = (codes = []) => {
	const seen = new Set();
	const unique = [];
	codes.forEach((code) => {
		const normalized = normalizeStoreCode(code).toUpperCase();
		if (!normalized || seen.has(normalized)) return;
		seen.add(normalized);
		unique.push(normalizeStoreCode(code));
	});
	return unique;
};

const lookupKeysForValue = (value) => {
	const normalized = normalizeStoreCode(value).toUpperCase();
	if (!normalized) return [];
	const compact = normalized.replace(/\s+/g, "");
	return compact && compact !== normalized ? [normalized, compact] : [normalized];
};

const resolveKnownStore = (raw, knownByNormalized) => {
	const keys = lookupKeysForValue(raw);
	for (const key of keys) {
		if (knownByNormalized.has(key)) return knownByNormalized.get(key);
	}
	return null;
};

const addStoreAlias = (knownByNormalized, alias, actualCode) => {
	lookupKeysForValue(alias).forEach((key) => {
		if (!knownByNormalized.has(key)) knownByNormalized.set(key, actualCode);
	});
};

export const parseStoreGroupingWorkbook = (data) => {
	const workbook = XLSX.read(data, { type: "array" });
	const sheetName = workbook.SheetNames[0];
	if (!sheetName) throw new Error("Excel file has no sheets");

	const sheet = workbook.Sheets[sheetName];
	const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false, blankrows: false });
	const emptyLists = {
		[GROUP_KEYS.to_allocate]: [],
		[GROUP_KEYS.to_exclude]: [],
		[GROUP_KEYS.foc_stores]: [],
	};

	if (!rows.length) return emptyLists;

	const firstRowKeys = Object.keys(rows[0] || {});
	const columnMap = {};
	firstRowKeys.forEach((header) => {
		const key = matchHeaderToKey(header);
		if (key && !columnMap[key]) columnMap[key] = header;
	});

	if (!Object.keys(columnMap).length) {
		throw new Error("Could not find To Allocate, To Exclude or FOC Stores columns");
	}

	const lists = { ...emptyLists };
	rows.forEach((row) => {
		Object.keys(lists).forEach((key) => {
			const header = columnMap[key];
			if (!header) return;
			const code = normalizeStoreCode(row[header]);
			if (code) lists[key].push(code);
		});
	});

	return {
		[GROUP_KEYS.to_allocate]: uniqueCodes(lists[GROUP_KEYS.to_allocate]),
		[GROUP_KEYS.to_exclude]: uniqueCodes(lists[GROUP_KEYS.to_exclude]),
		[GROUP_KEYS.foc_stores]: uniqueCodes(lists[GROUP_KEYS.foc_stores]),
	};
};

export const applyStoreGroupingUpload = ({ uploadedLists, knownStores = [], storeFullDataList = [] }) => {
	const knownByNormalized = new Map();

	const addKnownCode = (code) => {
		const actual = String(code ?? "").trim();
		if (!actual) return;
		addStoreAlias(knownByNormalized, actual, actual);
	};

	(storeFullDataList || []).forEach((store) => {
		const actual = String(store?.[storeMastStoreCodeKey] ?? "").trim();
		if (!actual) return;
		addKnownCode(actual);
		addStoreAlias(knownByNormalized, store?.[storeMastShortNameKey], actual);
	});
	(knownStores || []).forEach((code) => addKnownCode(code));

	const unknown = [];
	const assigned = new Map();
	const storeColumns = new Map();

	const assignBucket = (rawCodes, bucket) => {
		uniqueCodes(rawCodes).forEach((raw) => {
			const matched = resolveKnownStore(raw, knownByNormalized);
			const actual = matched || normalizeStoreCode(raw);
			if (!matched) unknown.push(raw);

			const columns = storeColumns.get(actual) || new Set();
			columns.add(bucket);
			storeColumns.set(actual, columns);

			if (columns.size === 1) assigned.set(actual, bucket);
			else assigned.delete(actual);
		});
	};

	assignBucket(uploadedLists?.[GROUP_KEYS.to_allocate] || [], GROUP_KEYS.to_allocate);
	assignBucket(uploadedLists?.[GROUP_KEYS.to_exclude] || [], GROUP_KEYS.to_exclude);
	assignBucket(uploadedLists?.[GROUP_KEYS.foc_stores] || [], GROUP_KEYS.foc_stores);

	const duplicates = [];
	storeColumns.forEach((columns, store) => {
		if (columns.size > 1) {
			duplicates.push({
				store,
				columns: [...columns].map((key) => storeGroupingtypes[key]?.name || key),
			});
		}
	});

	const grouping = {
		[GROUP_KEYS.to_allocate]: [],
		[GROUP_KEYS.to_exclude]: [],
		[GROUP_KEYS.foc_stores]: [],
	};

	assigned.forEach((bucket, store) => {
		grouping[bucket].push(store);
	});

	return { grouping, unknown, duplicates, assignedCount: assigned.size };
};

export const downloadStoreGroupingSample = () => {
	const worksheet = XLSX.utils.aoa_to_sheet([SAMPLE_HEADERS]);
	worksheet["!cols"] = SAMPLE_HEADERS.map(() => ({ wch: 18 }));
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "Store Grouping");
	XLSX.writeFile(workbook, "store_grouping_sample.xlsx");
};

export const readStoreGroupingFile = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (event) => {
			try {
				resolve(parseStoreGroupingWorkbook(new Uint8Array(event.target.result)));
			} catch (error) {
				reject(error);
			}
		};
		reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
		reader.readAsArrayBuffer(file);
	});
